/**
 * KAN-950: collapsed state of the tutorial button, kept in localStorage.
 * Without a stored value the button starts collapsed on narrow screens
 * (below the "sm" breakpoint). Storage errors (private mode, quota) are
 * ignored: state stays in memory.
 */
import { useCallback, useState } from 'react';

export const TUTORIAL_COLLAPSED_KEY = 'tutorialButtonCollapsed';
const NARROW_SCREEN_QUERY = '(max-width: 639px)';

function isNarrowScreen(): boolean {
  try {
    return typeof window.matchMedia === 'function'
      ? window.matchMedia(NARROW_SCREEN_QUERY).matches
      : false;
  } catch {
    return false;
  }
}

function readStored(): string | null {
  try {
    return localStorage.getItem(TUTORIAL_COLLAPSED_KEY);
  } catch {
    return null;
  }
}

function readCollapsed(): boolean {
  const stored = readStored();
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return isNarrowScreen();
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(TUTORIAL_COLLAPSED_KEY, String(value));
  } catch {
    /* storage unavailable: keep in memory only */
  }
}

export function useTutorialCollapsed(): [boolean, (value: boolean) => void] {
  const [collapsed, setCollapsedState] = useState<boolean>(readCollapsed);

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    writeCollapsed(value);
  }, []);

  return [collapsed, setCollapsed];
}
